import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { register as registerUser } from 'services/authService';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';

const registerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email(),
  password: z.string().min(8, "Password must be at least 8 characters")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, "Password must contain at least one uppercase letter, one lowercase letter, and one number"),
  password_confirm: z.string().min(1, "Please confirm your password"),
  demo_request: z.boolean().default(false),
  country: z.string().optional(),
  sector: z.string().optional(),
}).refine((data) => data.password === data.password_confirm, {
  message: "Passwords do not match",
  path: ["password_confirm"],
}).refine((data) => !data.demo_request || (data.demo_request && data.country && data.sector), {
  message: "Country and sector are required when requesting a demo",
  path: ["demo_request"],
});

export type RegisterData = z.infer<typeof registerSchema>;

interface DemoOptions {
  countries: string[];
  sectors: string[];
}

const RegisterPage: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<RegisterData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      demo_request: false,
    },
  });
  const navigate = useNavigate();
  const [demoOptions, setDemoOptions] = useState<DemoOptions>({ countries: [], sectors: [] });
  const [loading, setLoading] = useState(false);
  
  const demoRequest = watch('demo_request');

  useEffect(() => {
    const fetchDemoOptions = async () => {
      try {
        const response = await api.get('/auth/demo-options');
        setDemoOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch demo options', error);
      }
    };
    
    fetchDemoOptions();
  }, []);

  const onSubmit = async (data: RegisterData) => {
    setLoading(true);
    try {
      await registerUser(data);
      navigate('/login');
    } catch (error) {
      console.error('Registration failed', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background-light">
      <div className="w-full max-w-md p-8 space-y-6 bg-surface rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center text-text-primary">Register</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...register('name')} />
            {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...register('email')} />
            {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" {...register('password')} />
            {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
          </div>
          
          <div>
            <Label htmlFor="password_confirm">Confirm Password</Label>
            <Input id="password_confirm" type="password" {...register('password_confirm')} />
            {errors.password_confirm && <p className="text-red-500 text-sm">{errors.password_confirm.message}</p>}
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="demo_request" 
              {...register('demo_request')}
            />
            <Label htmlFor="demo_request" className="text-sm font-medium">
              Request a Demo
            </Label>
          </div>
          {errors.demo_request && (
            <p className="text-red-500 text-sm">{errors.demo_request.message}</p>
          )}
          
          {demoRequest && (
            <>
               <div>
                 <Label htmlFor="country">Country</Label>
                 <Select onValueChange={(value) => setValue('country', value)}>
                   <SelectTrigger>
                     <SelectValue placeholder="Select your country" />
                   </SelectTrigger>
                   <SelectContent>
                     {demoOptions.countries.map((country) => (
                       <SelectItem key={country} value={country}>
                         {country}
                       </SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
                 {errors.country && <p className="text-red-500 text-sm">{errors.country.message}</p>}
               </div>
               
               <div>
                 <Label htmlFor="sector">Sector of Activity</Label>
                 <Select onValueChange={(value) => setValue('sector', value)}>
                   <SelectTrigger>
                     <SelectValue placeholder="Select your sector" />
                   </SelectTrigger>
                   <SelectContent>
                     {demoOptions.sectors.map((sector) => (
                       <SelectItem key={sector} value={sector}>
                         {sector}
                       </SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
                 {errors.sector && <p className="text-red-500 text-sm">{errors.sector.message}</p>}
               </div>
            </>
          )}
          
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </Button>
          
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Already have an account?{' '}
              <a href="/login" className="text-primary hover:underline">
                Login here
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;